import React, { useMemo, useState } from "react"

import { useAudioSignedUrlTransport } from "@/audio"
import {
  useAnnotationsWithMetricOutputsTransport,
  useHumanReviewProjectsTransport,
  useReviewMetricTransportInvalidation,
  useUpdateReviewAnnotationTransport,
} from "@/human-feedback/transport"
import { MetricVersionTag } from "@/metrics/metric-display"
import { useSimulationOutputTransport } from "@/runs"
import { Skeleton, Tooltip } from "@coval/ui"
import { addToast } from "@heroui/react"
import { RotateCw, Star } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import {
  AudioPlayer,
  type AudioRegionParams,
} from "@/components/audio-player/AudioPlayer"
import type { Annotation } from "@/types/api/Annotation"
import type { HumanReviewProject } from "@/types/api/HumanReviewProject"
import { toMetricOutputDisplaySource } from "@/types/RunMetricOutput"

import { ALL_PROJECTS_FILTER, formatDate } from "../../utils/filterSimulations"
import { useTestDrawer, useTestMetricRuntime } from "../contexts"
import type { ExperimentTestResult } from "../types"
import { parseOffsets } from "../utils/parseOffsets"
import { TranscriptDetailView } from "./TranscriptDetailView"

const log = logger("AudioMetricsSimulationDetailView")
const METRIC_REGION_COLOR = "rgba(255, 0, 0, 0.7)"
const TEST_REGION_COLOR = "rgba(245, 158, 11, 0.7)"
const HUMAN_REGION_COLOR = "rgba(147, 51, 234, 0.6)"

type SegmentSource = "metric" | "test" | `human:${string}`

interface HumanRegionAnnotation {
  ground_truth_subvalues_by_timestamp?: unknown
  groundTruthSubvaluesByTimestamp?: unknown
}

function getHumanRegionsAsAudioParams(
  annotation: HumanRegionAnnotation | null | undefined,
): AudioRegionParams[] {
  if (!annotation) {
    return []
  }
  const raw =
    annotation.ground_truth_subvalues_by_timestamp ??
    annotation.groundTruthSubvaluesByTimestamp
  if (raw == null) {
    return []
  }
  const arr: Array<{ start_offset: number; end_offset?: number }> =
    parseOffsets(raw)
  return arr.map((s, idx) => ({
    id: `human:${idx}`,
    start: s.start_offset,
    end: Math.max(s.end_offset ?? s.start_offset + 0.05, s.start_offset + 0.05),
    color: HUMAN_REGION_COLOR,
    drag: false,
    resize: false,
  }))
}

interface AudioMetricsSimulationDetailViewProps {
  currentIndex: number
  totalCount: number
  onNext: () => void
  onPrevious: () => void
  selectedProjectId?: string
  experimentResult?: ExperimentTestResult | null
  onRunExperiment: () => void
  isRunningExperiment: boolean
}

export const AudioMetricsSimulationDetailView: React.FC<
  AudioMetricsSimulationDetailViewProps
> = ({
  currentIndex,
  totalCount,
  onNext,
  onPrevious,
  selectedProjectId = ALL_PROJECTS_FILTER,
  experimentResult,
  onRunExperiment,
  isRunningExperiment,
}) => {
  const { metric } = useTestMetricRuntime()
  const { trackEvent } = useAnalytics()
  const { selectedSimulationId } = useTestDrawer()
  const simulationId = selectedSimulationId ?? ""
  const invalidateReviewMetricTransport = useReviewMetricTransportInvalidation()
  const { data: annotationsData, isLoading: isLoadingAnnotations } =
    useAnnotationsWithMetricOutputsTransport({ metricId: metric?.id })

  // Fetch human review projects for filtering
  const { data: humanReviewProjects } = useHumanReviewProjectsTransport()

  const updateAnnotationMutation = useUpdateReviewAnnotationTransport()

  const [isSettingPrimary, setIsSettingPrimary] = useState(false)

  const handleSetPrimary = async (reviewId: string) => {
    const otherIds = allAnnotationsForSimulation
      .map((a) => a.id)
      .filter((id) => id !== reviewId)
    setIsSettingPrimary(true)
    try {
      await Promise.all([
        ...otherIds.map((id) =>
          updateAnnotationMutation.mutateAsync({
            annotationId: id,
            priority: "STANDARD",
          }),
        ),
        updateAnnotationMutation.mutateAsync({
          annotationId: reviewId,
          priority: "PRIMARY",
        }),
      ])
      invalidateReviewMetricTransport()
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unknown error"
      log.error("Failed to set primary annotation", message)
      addToast({
        title: "Failed to set primary review",
        description: "Something went wrong. Please try again.",
        color: "danger",
      })
    } finally {
      setIsSettingPrimary(false)
    }
  }

  const { data: simulationOutput, isLoading: isLoadingSimulationOutput } =
    useSimulationOutputTransport(simulationId, { enabled: !!simulationId })

  const allAnnotationsForSimulation = useMemo(() => {
    const all = annotationsData?.annotations[metric?.id || ""] ?? []
    return all.filter(
      (a: Annotation) => a.simulation_output_id === simulationId && !a.archived,
    )
  }, [annotationsData, metric?.id, simulationId])

  const annotations = useMemo(() => {
    if (selectedProjectId === ALL_PROJECTS_FILTER || !humanReviewProjects) {
      return allAnnotationsForSimulation
    }
    const project = humanReviewProjects.find(
      (p: HumanReviewProject) => p.ulid === selectedProjectId,
    )
    if (!project) {
      return allAnnotationsForSimulation
    }
    return allAnnotationsForSimulation.filter(
      (a: Annotation) =>
        project.linked_simulation_ids.includes(a.simulation_output_id) &&
        project.assignees.includes(a.assignee),
    )
  }, [allAnnotationsForSimulation, selectedProjectId, humanReviewProjects])
  const metricOutput =
    annotationsData?.metricOutputs[metric?.id || ""]?.[simulationId]

  const {
    data: signedUrl,
    isError: isErrorAudio,
    isLoading: isLoadingSignedUrl,
  } = useAudioSignedUrlTransport(
    {
      bucket: simulationOutput?.simulation_s3_bucket || "",
      audioKey: simulationOutput?.simulation_s3_key || "",
      asTestCase: false,
    },
    {
      enabled:
        !!simulationOutput?.simulation_s3_bucket &&
        !!simulationOutput?.simulation_s3_key,
    },
  )

  const metricRegions = useMemo((): AudioRegionParams[] => {
    if (
      !metric ||
      isLoadingAnnotations ||
      isLoadingSimulationOutput ||
      isLoadingSignedUrl ||
      metricOutput === undefined
    ) {
      return []
    }
    const displayMetricOutput = toMetricOutputDisplaySource(metricOutput)
    if (!displayMetricOutput) {
      return []
    }
    const regions = metric.getAudioRegions?.(displayMetricOutput) ?? []
    return regions.map((r) => ({
      ...r,
      color: METRIC_REGION_COLOR,
      drag: false,
      resize: false,
    }))
  }, [
    metric,
    metricOutput,
    isLoadingAnnotations,
    isLoadingSimulationOutput,
    isLoadingSignedUrl,
  ])

  const testRegions = useMemo((): AudioRegionParams[] => {
    if (!metric || !experimentResult?.rawMetricOutput) {
      return []
    }
    const regions =
      metric.getAudioRegions?.(experimentResult.rawMetricOutput) ?? []
    return regions.map((r) => ({
      ...r,
      color: TEST_REGION_COLOR,
      drag: false,
      resize: false,
    }))
  }, [metric, experimentResult])

  const humanRegionsByAnnotation = useMemo(
    () =>
      new Map(annotations.map((a) => [a.id, getHumanRegionsAsAudioParams(a)])),
    [annotations],
  )

  const [hoveredSegmentSource, setHoveredSegmentSource] =
    useState<SegmentSource | null>(null)
  const [pinnedSegmentSource, setPinnedSegmentSource] =
    useState<SegmentSource | null>(null)

  const displayedRegions = useMemo((): AudioRegionParams[] => {
    const source = pinnedSegmentSource ?? hoveredSegmentSource
    if (source === "metric") {
      return metricRegions
    }
    if (source === "test") {
      return testRegions
    }
    if (source?.startsWith("human:")) {
      const annotationId = source.slice("human:".length)
      return humanRegionsByAnnotation.get(annotationId) ?? []
    }
    return []
  }, [
    pinnedSegmentSource,
    hoveredSegmentSource,
    metricRegions,
    testRegions,
    humanRegionsByAnnotation,
  ])

  const renderReviewSection = () => {
    const outputVersionStatus =
      metricOutput && metric
        ? metric.getOutputVersionStatus(metricOutput)
        : null
    return (
      <div className="flex flex-col gap-4 p-4 border-b border-divider shrink-0">
        {signedUrl && !isErrorAudio && (
          <div className="shrink-0">
            <AudioPlayer
              signedUrl={signedUrl}
              isErrorAudio={isErrorAudio}
              onRegionPlayed={() => {}}
              regions={displayedRegions}
              onIsPlayingChange={() => {}}
              simulationOutputId={simulationId}
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          {/* Left column – metric output + test result stacked */}
          <div className="flex flex-col gap-2">
            {/* Metric output – hover/click shows metric segments on waveform */}
            <div
              className="space-y-2 rounded-lg border border-divider p-3 transition-colors hover:border-coval-border hover:bg-surface-1 cursor-pointer"
              onMouseEnter={() => setHoveredSegmentSource("metric")}
              onMouseLeave={() => setHoveredSegmentSource(null)}
              onClick={() =>
                setPinnedSegmentSource((prev) =>
                  prev === "metric" ? null : "metric",
                )
              }
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  setPinnedSegmentSource((prev) =>
                    prev === "metric" ? null : "metric",
                  )
                }
              }}
              aria-label="Metric output – hover or click to show segments on waveform"
            >
              <h3 className="text-sm font-semibold text-foreground">
                Metric output
              </h3>
              {metricOutput && metric && outputVersionStatus?.isStale && (
                <div className="mt-1 space-y-1.5">
                  <MetricVersionTag
                    metric={metric}
                    metricOutput={metricOutput}
                    precomputedStatus={outputVersionStatus}
                    variant="full"
                  />
                  {metric.getVersionDiff(metricOutput).map((diff) => (
                    <div key={diff.field} className="text-xs space-y-0.5">
                      <p className="text-coval-text-muted font-medium">
                        {diff.field}
                      </p>
                      <p className="text-coval-text-muted whitespace-pre-wrap">
                        {diff.previous}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {(() => {
                const metricOutputDate = formatDate(
                  metricOutput?.created_at || null,
                )
                return (
                  metricOutputDate && (
                    <p className="text-xs text-coval-text-muted">
                      {metricOutputDate}
                    </p>
                  )
                )
              })()}
            </div>

            {/* Test result – hover/click shows test segments on waveform */}
            {isRunningExperiment ? (
              <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50/30 p-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Test result
                </h3>
                <Skeleton className="h-3 w-24 rounded-md" />
              </div>
            ) : experimentResult ? (
              <div
                className="space-y-2 rounded-lg border border-amber-300 p-3 transition-colors hover:border-amber-400 hover:bg-amber-50/50 cursor-pointer"
                onMouseEnter={() => setHoveredSegmentSource("test")}
                onMouseLeave={() => setHoveredSegmentSource(null)}
                onClick={() =>
                  setPinnedSegmentSource((prev) =>
                    prev === "test" ? null : "test",
                  )
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    setPinnedSegmentSource((prev) =>
                      prev === "test" ? null : "test",
                    )
                  }
                }}
                aria-label="Test result – hover or click to show segments on waveform"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">
                    Test result
                  </h3>
                  <Tooltip content="Re-test" side="top">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        trackEvent(
                          TRACKING_EVENTS.METRICS.TEST_METRIC_BUTTON_CLICKED,
                          { metric_id: metric?.id },
                        )
                        onRunExperiment()
                      }}
                      className="text-coval-text-muted hover:text-coval-text-muted transition-colors"
                      aria-label="Re-test this simulation"
                    >
                      <RotateCw className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                </div>
                {experimentResult.errorStatus === "FAILED" ? (
                  <p className="text-xs text-coval-danger-text">
                    {experimentResult.errorMessage || "Test failed"}
                  </p>
                ) : (
                  experimentResult.value && (
                    <p className="text-xs text-coval-text-muted">
                      {experimentResult.value}
                    </p>
                  )
                )}
              </div>
            ) : null}
          </div>

          {/* Human reviews – stacked in the right column */}
          <div className="flex flex-col gap-2">
            {isLoadingAnnotations ? (
              <div className="space-y-2 rounded-lg border border-divider p-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Human review
                </h3>
                <p className="text-sm text-coval-text-muted">Loading...</p>
              </div>
            ) : annotations.length > 0 ? (
              annotations.map((annotation) => {
                const source: SegmentSource = `human:${annotation.id}`
                const annotationDate = formatDate(annotation.last_updated_at)
                return (
                  <div
                    key={annotation.id}
                    className="group space-y-2 rounded-lg border border-divider p-3 transition-colors hover:border-coval-border hover:bg-surface-1 cursor-pointer"
                    onMouseEnter={() => setHoveredSegmentSource(source)}
                    onMouseLeave={() => setHoveredSegmentSource(null)}
                    onClick={() =>
                      setPinnedSegmentSource((prev) =>
                        prev === source ? null : source,
                      )
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        setPinnedSegmentSource((prev) =>
                          prev === source ? null : source,
                        )
                      }
                    }}
                    aria-label={`Human review${annotation.assignee ? ` by ${annotation.assignee}` : ""}${annotation.priority === "PRIMARY" ? " (primary)" : ""} – hover or click to show segments on waveform`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-foreground">
                        Human review
                      </h3>
                      {annotations.length > 1 && (
                        <Tooltip
                          content={
                            annotation.priority === "PRIMARY"
                              ? "Counted in stats"
                              : "Set as primary"
                          }
                          side="top"
                        >
                          <button
                            onKeyDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (annotation.priority !== "PRIMARY") {
                                void handleSetPrimary(annotation.id)
                              }
                            }}
                            disabled={
                              annotation.priority === "PRIMARY" ||
                              isSettingPrimary
                            }
                            className={`transition-all ${
                              annotation.priority === "PRIMARY"
                                ? "text-coval-warning-text"
                                : "text-coval-text-muted opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:text-coval-warning-text focus-visible:ring-2 focus-visible:ring-coval-warning-border focus-visible:outline-none"
                            } ${isSettingPrimary ? "opacity-50" : ""}`}
                            aria-label={
                              annotation.priority === "PRIMARY"
                                ? "Counted in stats"
                                : "Set as primary"
                            }
                          >
                            <Star
                              className="w-3 h-3"
                              fill={
                                annotation.priority === "PRIMARY"
                                  ? "currentColor"
                                  : "none"
                              }
                            />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                    {annotation.annotations?.custom_explanation && (
                      <p className="text-sm text-foreground">
                        {annotation.annotations.custom_explanation}
                      </p>
                    )}
                    {annotation.assignee && (
                      <p className="text-xs text-coval-text-muted">
                        {annotation.assignee}
                      </p>
                    )}
                    {annotationDate && (
                      <p className="text-xs text-coval-text-muted">
                        {annotationDate}
                      </p>
                    )}
                  </div>
                )
              })
            ) : (
              <div className="space-y-2 rounded-lg border border-divider p-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Human review
                </h3>
                <p className="text-sm text-foreground">No annotation</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (!selectedSimulationId) {
    return null
  }

  return (
    <TranscriptDetailView
      currentIndex={currentIndex}
      totalCount={totalCount}
      onNext={onNext}
      onPrevious={onPrevious}
      renderReviewSection={renderReviewSection}
    />
  )
}
